import { useQuery } from 'react-query';
import type { CSSProperties } from 'react';
import CardRelatorio from './CardRelatorio';
import Valor from './Valor';
import { GraficoDesfechoReengajamento } from './GraficoDesfechoReengajamento';
import axios from 'axios';
import Skeleton from '../../components/layout/Skeleton';
import { translateFilter } from './util';
import { parse, differenceInCalendarDays } from 'date-fns';
import Grid, { GridItem } from '@/components/layout/Grid';

type Props = {
  periodo: [string, string, string];
  canal_id: number[];
  style?: CSSProperties;
};

// Desfechos factuais de um reengajamento disparado (cutucada). Cada um é uma
// saída mutuamente excludente do ciclo; "resolvido" é o implícito (cliente
// respondeu à cutucada). O significado de "sem resposta" depende de como o
// usuário ligou a rota (encaminhamento humano = positivo; tchau-e-fecha =
// perda) — o relatório mostra o fato, a leitura é de quem configurou o fluxo.
// "sem_desfecho" (motivo reengajamento_timeout) é o resíduo: chat ainda com o
// bot que fechou inativo com a cutucada pendente, sem o ciclo chegar a desfecho
// (legado/erro). Ciclos interrompidos por atendimento humano fecham como
// "inatividade" comum — não são abandono do bot.
const DESFECHOS = [
  { key: 'resolvido', label: 'Resolvido', color: '#22c55e', qtd: 'qtd_chats_reengajamento_resolvido', taxa: 'taxa_resolvido_reengajamento' },
  { key: 'sem_resposta', label: 'Sem resposta', color: '#8b5cf6', qtd: 'qtd_chats_reengajamento_sem_resposta', taxa: 'taxa_sem_resposta_reengajamento' },
  { key: 'ajuda', label: 'Pedido de ajuda', color: '#3b82f6', qtd: 'qtd_chats_reengajamento_ajuda', taxa: 'taxa_ajuda_reengajamento' },
  { key: 'recusa', label: 'Recusa', color: '#f59e0b', qtd: 'qtd_chats_reengajamento_recusado', taxa: 'taxa_recusa_reengajamento' },
  { key: 'timeout', label: 'Sem desfecho', color: '#ef4444', qtd: 'qtd_chats_reengajamento_timeout', taxa: 'taxa_timeout_reengajamento' },
] as const;

// Deriva a granularidade do eixo de tempo a partir do intervalo selecionado.
// Retorna null quando o intervalo é um único dia (1 bucket) — nesse caso o card
// mostra a lista agregada em vez do gráfico.
function granularidadeDoPeriodo(
  periodo: [string, string, string],
): 'data_criacao_dia' | 'data_criacao_semana' | 'data_criacao_mes' | null {
  const d1 = parse(periodo[1], 'yyyy-MM-dd', new Date());
  const d2 = parse(periodo[2], 'yyyy-MM-dd', new Date());
  const dias = differenceInCalendarDays(d2, d1);
  if (dias <= 1) return null; // 1 bucket → lista agregada
  if (dias <= 14) return 'data_criacao_dia';
  if (dias <= 90) return 'data_criacao_semana';
  return 'data_criacao_mes';
}

const CardRelatorioAbandonoReengajamento = ({
  periodo,
  canal_id,
  style,
}: Props) => {
  const granularidade = granularidadeDoPeriodo(periodo);
  const multiBucket = granularidade !== null;

  const q = useQuery(
    ['taxaAbandonoReengajamento', periodo, canal_id],
    async () => {
      const qs = new URLSearchParams();
      qs.append(
        'facts',
        [
          'qtd_chats_reengajamento_disparado',
          'qtd_total_chats',
          ...DESFECHOS.flatMap((d) => [d.qtd, d.taxa]),
        ].join(','),
      );
      qs.append('filter', translateFilter({ periodo, canal_id }));

      const res = await axios.get(
        '/api/relatorios/chat_atendimento?' + qs.toString(),
      );
      return res.data.result[0];
    },
    { refetchOnWindowFocus: false },
  );

  const disparado = q.data?.qtd_chats_reengajamento_disparado ?? 0;
  const total = q.data?.qtd_total_chats ?? 0;

  // Linha de contexto no topo: os desfechos abaixo são % dos chats cutucados;
  // sem ela, o card passa a impressão de que os percentuais são sobre todos os
  // chats da fila de relatórios.
  const resumoReengajados = q.isSuccess && total > 0 && (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: '0.4rem',
        justifyContent: 'center',
      }}
    >
      <span style={{ color: 'gray', fontSize: '0.8rem' }}>Reengajados:</span>
      <Valor
        valor={(disparado / total) * 100}
        append="% dos chats"
        digits={1}
        valueSize="1.6rem"
        fractionSize="1rem"
      />
      <span style={{ color: 'gray', fontSize: '0.8rem' }}>
        ({disparado} de {total})
      </span>
    </div>
  );

  return (
    <CardRelatorio
      titulo="Desfecho do reengajamento"
      style={style}
      mostrarVisualizador={true}
      filtroVisualizador={{ periodo, canal_id }}
      metricasVisualizador={[
        'qtd_chats_reengajamento_disparado',
        'qtd_total_chats',
        ...DESFECHOS.flatMap((d) => [d.qtd, d.taxa]),
      ]}
      dimensoesVisualizador={['data_criacao_mes', 'canal_tipo']}
      hrefVisualizador="/api/relatorios/chat_atendimento"
      notaVisualizador="Desfechos factuais de cada reengajamento disparado (cutucada), mutuamente excludentes. 'Sem resposta' = tentativas esgotadas e seguiu a rota configurada (típic.: atendimento humano — positivo se for o caso). 'Resolvido' = o cliente respondeu à cutucada. 'Sem desfecho' = o chat ainda com o bot fechou inativo com a cutucada pendente, sem o ciclo chegar a desfecho (caso de borda). Todas as taxas têm como denominador os chats cutucados."
      info={`A linha do topo mostra os chats cutucados vs o total de chats do período. Os percentuais de desfecho têm como denominador os chats cutucados.`}
      csv
    >
      {resumoReengajados}
      {multiBucket ? (
        // Mais de um bucket no período: gráfico de área empilhada (contagem por
        // desfecho ao longo do tempo). A lista agregada fica para o caso de
        // período de um único dia.
        <GraficoDesfechoReengajamento
          periodo={periodo}
          canal_id={canal_id}
          granularidade={granularidade!}
        />
      ) : q.isFetching ? (
        <Skeleton format="box" />
      ) : (
        <Grid style={{ width: '100%', padding: '2rem' }}>
          {DESFECHOS.map((d) => {
            const taxa = q.data?.[d.taxa];
            const qtd = q.data?.[d.qtd] ?? 0;
            return (
              <GridItem sm={12} md={4} key={d.key}>
                <div style={{ color: 'gray', fontSize: '0.8rem', textAlign: 'center' }}>{d.label}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', justifyContent: 'center' }}>
                  <Valor
                    valor={taxa != null ? taxa * 100 : null}
                    append="%"
                    digits={1}
                    color={d.color}
                  />
                  <span style={{ color: 'gray', fontSize: '0.8rem' }}>
                    {qtd} de {disparado}
                  </span>
                </div>
              </GridItem>
            );
          })}
        </Grid>
      )}
    </CardRelatorio>
  );
};

export default CardRelatorioAbandonoReengajamento;